<template>
  <v-menu :offset="[0, 8]" location="bottom end" class="notification-dropdown" :close-on-content-click="false">
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        variant="text"
        color="secondary"
        size="small"
        density="comfortable"
        class="navbar-icon-btn"
        aria-label="Notifications"
      >
        <v-badge v-if="hasActiveSyncs" :content="totalNotificationCount" color="error" floating>
          <v-icon icon="notifications" size="22" />
        </v-badge>
        <v-icon v-else icon="notifications" size="22" />
      </v-btn>
    </template>
    <div class="notification-content">
      <div class="notification-wrapper">
        <!-- Header -->
        <div class="notification-header">
          <div class="notification-header-title">
            <h3 class="notification-title">Notifications</h3>
            <p class="notification-subtitle">{{ syncStatusLabel }}</p>
          </div>
          <button type="button" class="mark-all-read-btn" @click="markAllRead">
            <v-icon icon="done_all" size="16" />
            <span class="mark-all-read-btn__label">Mark all read</span>
          </button>
        </div>

        <!-- Notifications List -->
        <div class="notification-list">
          <div v-if="filteredJobs.length === 0" class="notification-empty">
            <v-icon icon="notifications_none" size="28" class="notification-empty__icon" />
            <p class="notification-empty__text">No sync notifications right now</p>
          </div>

          <div v-for="job in filteredJobs" :key="job.source" class="notification-item">
            <!-- Icon Container -->
            <div class="notification-icon-container">
              <div class="notification-icon-wrapper" :class="getIconWrapperClass(job)">
                <PlatformIcon v-if="isSocialSource(job.source)" :platform="job.source" :size="22" />
                <v-icon
                  v-else
                  :icon="getJobIcon(job)"
                  size="20px"
                  :class="{ 'animate-spin': job.status === 'in_progress' }"
                />
              </div>
            </div>

            <!-- Content -->
            <div class="notification-content-wrapper">
              <!-- In Progress with Progress Bar -->
              <template v-if="job.status === 'in_progress' && job.progress > 0">
                <div class="notification-content-header">
                  <p class="notification-title-text">{{ job.title }}</p>
                  <span class="notification-percentage" :class="getPercentageColorClass(job)">
                    {{ job.progress }}%
                  </span>
                </div>
                <p class="notification-message" :title="job.message || 'Processing...'">
                  {{ job.message || 'Processing...' }}
                </p>
                <p class="notification-time">{{ getNotificationTime(job) }}</p>
                <div class="notification-progress-bar">
                  <div
                    class="notification-progress-fill"
                    :class="getProgressBarColorClass(job)"
                    :style="{ width: job.progress + '%' }"
                  >
                    <div v-if="job.status === 'in_progress'" class="progress-shimmer"></div>
                  </div>
                </div>
              </template>

              <!-- Pending Status -->
              <template v-else-if="job.status === 'queued' || job.status === 'paused'">
                <div class="notification-content-with-badge">
                  <div class="notification-content-simple">
                    <p class="notification-title-text">{{ job.title }}</p>
                    <p class="notification-message" :title="job.message || 'Pending initialization...'">
                      {{ job.message || 'Pending initialization...' }}
                    </p>
                    <p class="notification-time">{{ getNotificationTime(job) }}</p>
                  </div>
                  <div class="notification-status-badge">
                    <span class="status-badge-text">Pending</span>
                  </div>
                </div>
              </template>

              <!-- Completed Status -->
              <template v-else-if="job.status === 'completed' || job.status === 'success'">
                <div class="notification-content-with-badge">
                  <div class="notification-content-simple">
                    <p class="notification-title-text">{{ job.title }}</p>
                    <p class="notification-message" :title="job.message || 'Job completed'">
                      {{ job.message || 'Job completed' }}
                    </p>
                    <p class="notification-time">{{ getNotificationTime(job) }}</p>
                  </div>
                  <div class="notification-complete-icon" aria-label="Completed">
                    <v-icon icon="check" size="14" />
                  </div>
                </div>
              </template>

              <!-- Other Statuses with Progress -->
              <template v-else>
                <div class="notification-content-header">
                  <p class="notification-title-text">{{ job.title }}</p>
                  <span v-if="job.progress > 0" class="notification-percentage" :class="getPercentageColorClass(job)">
                    {{ job.progress }}%
                  </span>
                </div>
                <p class="notification-message" :title="job.message || 'Processing...'">
                  {{ job.message || 'Processing...' }}
                </p>
                <p class="notification-time">{{ getNotificationTime(job) }}</p>
                <div v-if="job.progress > 0" class="notification-progress-bar">
                  <div
                    class="notification-progress-fill"
                    :class="getProgressBarColorClass(job)"
                    :style="{ width: job.progress + '%' }"
                  ></div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

import PlatformIcon from '../../../PlatformIcon.vue'
import { websocketService } from '../../../../services/websocket'
import { useAuthStore } from '../../../../stores/auth-store'

// Sync notification state
const hasNewMessage = ref(false)
const newMessageSources = ref(new Set<string>())
const currentFilter = ref('all')
const jobs = ref<any[]>([])
const lastUpdateTime = ref('')
const recentlyCompletedJobs = ref(new Set<string>()) // Track recently completed jobs

// Auth store
const authStore = useAuthStore()

// Sync notification types

interface SyncJob {
  id: string
  title: string
  source: string
  status: string
  progress: number
  message: string
  start_time?: string
  end_time?: string
  eta?: string
  timestamp: string
}

// Computed properties
const hasActiveSyncs = computed(() => {
  return jobs.value.some(
    (job) =>
      job.status === 'in_progress' ||
      job.status === 'queued' ||
      job.status === 'paused' ||
      job.status === 'completed' ||
      job.status === 'success' ||
      (job.status === 'completed' && recentlyCompletedJobs.value.has(job.source)) ||
      (job.status === 'success' && recentlyCompletedJobs.value.has(job.source)),
  )
})

const filteredJobs = computed(() => {
  let filtered = jobs.value

  // Apply source-based filtering
  switch (currentFilter.value) {
    case 'ads':
      filtered = filtered.filter((job) =>
        ['facebook', 'google', 'tiktok', 'pinterest', 'snapchat', 'microsoft'].includes(job.source),
      )
      break
    case 'orders':
      filtered = filtered.filter((job) => job.source === 'shopify_orders')
      break
    case 'products':
      filtered = filtered.filter((job) => job.source === 'shopify_products')
      break
    case 'fees':
      filtered = filtered.filter((job) =>
        ['transaction_fee_calculation', 'fee_calculation', 'cost_calculation'].includes(job.source),
      )
      break
  }

  // Sort by priority: in_progress > warning/partial > paused > queued > failed > completed/success
  const priorityOrder = {
    in_progress: 0,
    warning: 1,
    partial: 1,
    paused: 2,
    queued: 3,
    failed: 4,
    completed: 5,
    success: 5, // Treat "success" same as "completed"
  }

  return filtered.sort((a, b) => {
    const priorityA = priorityOrder[a.status as keyof typeof priorityOrder] ?? 6
    const priorityB = priorityOrder[b.status as keyof typeof priorityOrder] ?? 6
    return priorityA - priorityB
  })
})

const totalNotificationCount = computed(() => {
  if (!hasActiveSyncs.value) return ''

  // Count active syncs by status including recently completed
  const activeCount = jobs.value.filter(
    (job) =>
      job.status === 'in_progress' ||
      job.status === 'queued' ||
      job.status === 'paused' ||
      job.status === 'completed' || // Always count completed jobs
      job.status === 'success' || // Always count "success" status
      (job.status === 'completed' && recentlyCompletedJobs.value.has(job.source)) || // Also count recently completed (redundant but kept)
      (job.status === 'success' && recentlyCompletedJobs.value.has(job.source)), // Also count recently completed success (redundant but kept)
  ).length

  if (activeCount === 0) return ''
  if (activeCount > 9) return '9+'
  return activeCount.toString()
})

const activeSyncCount = computed(() => {
  return jobs.value.filter(
    (job) =>
      job.status === 'in_progress' ||
      job.status === 'queued' ||
      job.status === 'paused' ||
      job.status === 'completed' ||
      job.status === 'success',
  ).length
})

const syncStatusLabel = computed(() => {
  const count = activeSyncCount.value
  if (count === 0) return 'You are all caught up'
  if (count === 1) return '1 active sync process'
  return `${count} active sync processes`
})

// Sync notification methods
const getOverallStatus = (): string => {
  if (jobs.value.some((job) => job.status === 'failed')) return 'failed'
  if (jobs.value.some((job) => job.status === 'in_progress')) return 'in_progress'
  if (jobs.value.some((job) => job.status === 'warning' || job.status === 'partial')) return 'warning'
  if (jobs.value.some((job) => job.status === 'completed' || job.status === 'success')) return 'completed'
  return 'queued'
}

const getOverallStatusColor = (): string => {
  const status = getOverallStatus()
  switch (status) {
    case 'failed':
      return 'red'
    case 'in_progress':
      return 'blue'
    case 'warning':
      return 'yellow'
    case 'completed':
      return 'green'
    default:
      return 'gray'
  }
}

const isNewUpdate = (source: string): boolean => {
  return newMessageSources.value.has(source)
}

const setFilter = (filter: string) => {
  currentFilter.value = filter
}

const getSourceInitial = (source: string): string => {
  return source.split('_')[0].charAt(0).toUpperCase()
}

const getProgressBarClasses = (status: string): string => {
  const baseClasses = 'h-2 rounded-full transition-all duration-300'

  switch (status) {
    case 'queued':
      return `${baseClasses} bg-slate-400`
    case 'in_progress':
      return `${baseClasses} bg-blue-500 animate-pulse`
    case 'paused':
      return `${baseClasses} bg-gray-400 bg-stripes`
    case 'completed':
    case 'success': // Map "success" to green like "completed"
      return `${baseClasses} bg-green-500`
    case 'failed':
      return `${baseClasses} bg-red-500`
    case 'warning':
    case 'partial':
      return `${baseClasses} bg-yellow-500`
    default:
      return `${baseClasses} bg-gray-400`
  }
}

const getRelativeTime = (timestamp: string): string => {
  const now = new Date()
  const jobTime = new Date(timestamp)
  const diffMs = now.getTime() - jobTime.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
  return `${Math.floor(diffMins / 1440)}d ago`
}

const getNotificationTime = (job: SyncJob): string => {
  const now = new Date()

  // Format time as HH:MM AM/PM
  const formatTime = (date: Date): string => {
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    const displayMinutes = minutes.toString().padStart(2, '0')
    return `${displayHours}:${displayMinutes} ${ampm}`
  }

  // Format duration
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`
    }
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (secs === 0) {
      return `${mins}m`
    }
    return `${mins}m ${secs}s`
  }

  if (job.status === 'in_progress' || job.status === 'queued' || job.status === 'paused') {
    // For active jobs, use start_time (fallback to timestamp if start_time not available)
    const timeToUse = job.start_time || job.timestamp
    if (!timeToUse) return ''

    const jobTime = new Date(timeToUse)
    const diffMs = now.getTime() - jobTime.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffMins / 1440)

    if (diffMins < 1) return 'Started just now'
    if (diffMins < 60) return `Started ${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`
    if (diffHours < 24) return `Started ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
    return `Started ${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
  } else if (job.status === 'completed' || job.status === 'success') {
    // For completed jobs, use end_time (fallback to timestamp if end_time not available)
    const timeToUse = job.end_time || job.timestamp
    if (!timeToUse) return ''

    const jobTime = new Date(timeToUse)
    const diffMs = now.getTime() - jobTime.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffDays = Math.floor(diffMins / 1440)

    // Calculate duration if both start_time and end_time are available
    let durationText = ''
    if (job.start_time && job.end_time) {
      const startTime = new Date(job.start_time)
      const endTime = new Date(job.end_time)
      const durationSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000)
      if (durationSeconds > 0) {
        durationText = `Duration: ${formatDuration(durationSeconds)}`
      }
    }

    // Format date and time
    const isToday = jobTime.toDateString() === now.toDateString()
    const isYesterday =
      diffDays === 1 && jobTime.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString()

    let timeText = ''
    if (isToday) {
      timeText = `Today at ${formatTime(jobTime)}`
    } else if (isYesterday) {
      timeText = `Yesterday at ${formatTime(jobTime)}`
    } else if (diffDays < 7) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      timeText = `${dayNames[jobTime.getDay()]} at ${formatTime(jobTime)}`
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      timeText = `${monthNames[jobTime.getMonth()]} ${jobTime.getDate()} at ${formatTime(jobTime)}`
    }

    // Return time with duration if available
    return durationText ? `${timeText} • ${durationText}` : timeText
  } else {
    // For other statuses, use timestamp
    if (!job.timestamp) return ''
    return getRelativeTime(job.timestamp)
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'queued':
      return 'slate'
    case 'in_progress':
      return 'blue'
    case 'paused':
      return 'neutral'
    case 'completed':
    case 'success': // Map "success" to green like "completed"
      return 'green'
    case 'failed':
      return 'red'
    case 'warning':
      return 'yellow'
    case 'partial':
      return 'yellow'
    default:
      return 'gray'
  }
}

// Get icon for job based on source and status
const isSocialSource = (source: string): boolean => {
  return ['facebook', 'google', 'tiktok', 'pinterest', 'snapchat', 'microsoft'].includes(source)
}

const getJobIcon = (job: SyncJob): string => {
  if (job.status === 'in_progress') {
    return 'sync'
  }
  if (job.status === 'queued' || job.status === 'paused') {
    return 'schedule'
  }
  if (job.status === 'completed' || job.status === 'success') {
    return 'check'
  }

  const sourceIcons: Record<string, string> = {
    facebook: 'public',
    google: 'shopping_bag',
    shopify_orders: 'inventory_2',
    shopify_products: 'inventory_2',
    transaction_fee_calculation: 'payments',
    fee_calculation: 'payments',
    cost_calculation: 'calculate',
  }

  return sourceIcons[job.source] || 'sync'
}

// Get icon wrapper class based on source/status
const getIconWrapperClass = (job: SyncJob): string => {
  if (job.status === 'completed' || job.status === 'success') {
    return 'icon-wrapper-completed'
  }
  if (job.status === 'queued' || job.status === 'paused') {
    return 'icon-wrapper-pending'
  }

  // Source-based colors
  const sourceColors: Record<string, string> = {
    facebook: 'icon-wrapper-facebook',
    google: 'icon-wrapper-google',
    shopify_orders: 'icon-wrapper-blue',
    shopify_products: 'icon-wrapper-blue',
    transaction_fee_calculation: 'icon-wrapper-blue',
    fee_calculation: 'icon-wrapper-blue',
    cost_calculation: 'icon-wrapper-blue',
  }

  return sourceColors[job.source] || 'icon-wrapper-blue'
}

// Get progress bar color class
const getProgressBarColorClass = (job: SyncJob): string => {
  if (job.source === 'google') {
    return 'progress-fill-orange'
  }
  return 'progress-fill-primary'
}

// Get percentage color class
const getPercentageColorClass = (job: SyncJob): string => {
  if (job.source === 'google') {
    return 'percentage-orange'
  }
  return 'percentage-primary'
}

// Mark all notifications as read
const markAllRead = () => {
  newMessageSources.value.clear()
  hasNewMessage.value = false
}

const handleWebSocketMessage = (event: any) => {
  try {
    // Prevent handling if event is from browser extension
    if (!event || !event.detail) {
      return
    }

    const data = event.detail

    // Extract source from notification_message or topic
    let source = data.notification_message?.source
    if (!source && data.topic) {
      // Extract source from topic like "sync:facebook" -> "facebook"
      const topicParts = data.topic.split(':')
      if (topicParts.length > 1) {
        source = topicParts[1]
      }
    }

    // Mark as new message for impressive feedback
    if (source) {
      hasNewMessage.value = true
      newMessageSources.value.add(source)

      // Auto-clear new message indicators after 10 seconds
      setTimeout(() => {
        newMessageSources.value.delete(source)
        if (newMessageSources.value.size === 0) {
          hasNewMessage.value = false
        }
      }, 10000)
    }

    updateJob(data)
    lastUpdateTime.value = new Date().toLocaleTimeString()
  } catch {
    // ignore malformed websocket payloads
  }
}

const updateJob = (data: any) => {
  // Extract source from notification_message or topic
  let source = data.notification_message?.source
  if (!source && data.topic) {
    // Extract source from topic like "sync:facebook" -> "facebook"
    const topicParts = data.topic.split(':')
    if (topicParts.length > 1) {
      source = topicParts[1]
    }
  }

  // If still no source, we can't process this job
  if (!source) {
    return
  }

  const existingJobIndex = jobs.value.findIndex((job) => job.source === source)
  const newJob = createJobFromNotification(data)

  if (existingJobIndex >= 0) {
    // Update existing job - update all fields to ensure reactivity
    const existingJob = jobs.value[existingJobIndex]
    existingJob.id = newJob.id
    existingJob.title = newJob.title
    existingJob.status = newJob.status
    existingJob.progress = newJob.progress
    existingJob.message = newJob.message
    existingJob.start_time = newJob.start_time
    existingJob.end_time = newJob.end_time
    existingJob.timestamp = newJob.timestamp
  } else {
    jobs.value.unshift(newJob)
  }

  // Track recently completed jobs (handle both "completed" and "success" for backward compatibility)
  if (newJob.status === 'completed' || newJob.status === 'success') {
    recentlyCompletedJobs.value.add(newJob.source)

    // Remove from recently completed after 5 minutes
    setTimeout(
      () => {
        recentlyCompletedJobs.value.delete(newJob.source)
      },
      5 * 60 * 1000,
    )
  }
}

const createJobFromNotification = (data: any): SyncJob => {
  const notification = data.notification_message || {}

  // Get progress from multiple possible locations
  let progress = notification.progress
  if (progress === undefined || progress === null) {
    progress = data.progress
  }
  if (progress === undefined || progress === null) {
    progress = 0
  }

  // Ensure progress is a number and within valid range
  progress = Math.max(0, Math.min(100, Number(progress)))

  // Determine status: use notification.status, or infer from progress/message
  let status = notification.status || data.status
  if (!status) {
    // If no status but progress is 100, assume completed
    if (progress === 100) {
      status = 'completed'
    } else if (progress > 0) {
      status = 'in_progress'
    } else {
      status = 'queued'
    }
  }

  // Extract source from notification_message or topic
  let source = notification.source || data.source
  if (!source && data.topic) {
    // Extract source from topic like "sync:facebook" -> "facebook"
    const topicParts = data.topic.split(':')
    if (topicParts.length > 1) {
      source = topicParts[1]
    }
  }
  if (!source) {
    source = 'unknown'
  }

  // Handle message field - can be string or object with detail property
  let messageText = ''
  if (typeof data.message === 'string') {
    messageText = data.message
  } else if (data.message?.detail) {
    messageText = data.message.detail
  } else if (notification.message) {
    messageText = notification.message
  } else if (data.notification_message?.message) {
    messageText = data.notification_message.message
  }

  // Set default message based on status if no message provided
  if (!messageText) {
    if (status === 'completed' || status === 'success') {
      messageText = 'Job completed'
    } else if (status === 'in_progress') {
      messageText = 'Processing...'
    } else if (status === 'queued' || status === 'paused') {
      messageText = 'Pending initialization...'
    } else {
      messageText = 'Job started'
    }
  }

  // Handle title - prefer title, then message, then notification name
  const title = data.title || notification.title || notification.name || messageText || 'Unknown Job'

  return {
    id: data.id || notification.id || `job-${Date.now()}`,
    title: title,
    source: source,
    status: status,
    progress: progress,
    message: messageText,
    start_time: notification.start_time || data.start_time,
    end_time: notification.end_time || data.end_time,
    timestamp: data.timestamp || notification.timestamp || new Date().toISOString(),
  }
}

// Lifecycle
const addWebSocketListeners = () => {
  window.addEventListener('websocket:message', handleWebSocketMessage, { passive: true })
}

const removeWebSocketListeners = () => {
  window.removeEventListener('websocket:message', handleWebSocketMessage)
}

let isConnecting = false
let lastToken: string | null = null

const connectWebSocket = async () => {
  // Prevent multiple simultaneous connection attempts
  if (isConnecting) {
    return
  }

  const currentToken = authStore.token

  if (!currentToken) {
    websocketService.disconnect()
    removeWebSocketListeners()
    lastToken = null
    return
  }

  // Only reconnect if token actually changed
  if (lastToken === currentToken && websocketService.connectionStatus === 'connected') {
    // Ensure listeners are attached even if already connected
    addWebSocketListeners()
    return
  }

  isConnecting = true

  try {
    // Only disconnect and reconnect if token changed
    if (lastToken !== currentToken) {
      removeWebSocketListeners()
      websocketService.disconnect()
      await websocketService.listenNotification()
      addWebSocketListeners()
      lastToken = currentToken
    } else if (websocketService.connectionStatus !== 'connected') {
      // Token is same but not connected, try to connect
      await websocketService.listenNotification()
      addWebSocketListeners()
    }
  } finally {
    isConnecting = false
  }
}

onMounted(async () => {
  lastToken = authStore.token
  await connectWebSocket()
  lastUpdateTime.value = new Date().toLocaleTimeString()
})

onUnmounted(() => {
  websocketService.disconnect()
  removeWebSocketListeners()
  lastToken = null
})

watch(
  () => authStore.token,
  async (newToken, oldToken) => {
    // Only reconnect if token actually changed
    if (newToken !== oldToken) {
      await connectWebSocket()
    }
  },
)
</script>

<style lang="scss" scoped>
.notification-content {
  padding: 0 !important;
  width: min(22.5rem, calc(100vw - 1.5rem)) !important;
  max-width: min(22.5rem, calc(100vw - 1.5rem)) !important;
  min-width: 0 !important;
}

.notification-wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 0.75rem;
  background: rgb(var(--v-theme-background-secondary));
  border: 1px solid rgb(var(--v-theme-background-border));
  box-shadow:
    0 12px 28px -8px color-mix(in srgb, rgb(var(--v-theme-text-primary)) 18%, transparent),
    0 4px 10px -4px color-mix(in srgb, rgb(var(--v-theme-text-primary)) 10%, transparent);
  overflow: hidden;
  width: 100%;
}

/* Header */
.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid rgb(var(--v-theme-background-border));
  background: rgb(var(--v-theme-background-element));
}

.notification-header-title {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1 1 auto;
}

.notification-title {
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.25;
  color: rgb(var(--v-theme-text-primary));
  margin: 0;
}

.notification-subtitle {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.3;
  color: rgb(var(--v-theme-text-secondary));
  margin: 0;
}

.mark-all-read-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  flex-shrink: 0;
  height: 1.75rem;
  padding: 0 0.65rem;
  border-radius: 0.5rem;
  background: rgb(var(--v-theme-background-secondary));
  border: 1px solid rgb(var(--v-theme-background-border));
  color: rgb(var(--v-theme-text-secondary));
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, rgb(var(--v-theme-primary)) 10%, rgb(var(--v-theme-background-secondary)));
    border-color: color-mix(in srgb, rgb(var(--v-theme-primary)) 35%, rgb(var(--v-theme-background-border)));
    color: rgb(var(--v-theme-primary));
  }
}

.mark-all-read-btn__label {
  white-space: nowrap;
}

/* Notifications List */
.notification-list {
  display: flex;
  flex-direction: column;
  max-height: min(28rem, 70vh);
  overflow-y: auto;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.75rem 1rem;
  text-align: center;
}

.notification-empty__icon {
  color: rgb(var(--v-theme-text-secondary));
  opacity: 0.7;
}

.notification-empty__text {
  margin: 0;
  font-size: 0.8125rem;
  color: rgb(var(--v-theme-text-secondary));
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid rgb(var(--v-theme-background-border));
  transition: background-color 0.15s ease;
}

.notification-item:hover {
  background: color-mix(in srgb, rgb(var(--v-theme-background-element)) 80%, transparent);
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-icon-container {
  flex-shrink: 0;
  padding-top: 0.125rem;
}

.notification-icon-wrapper {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.icon-wrapper-blue {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.icon-wrapper-facebook {
  background: rgba(24, 119, 242, 0.1);
  color: #1877f2;
}

.icon-wrapper-google {
  background: rgba(251, 146, 60, 0.1);
  color: #ea580c;
}

.icon-wrapper-pending {
  background: rgb(var(--v-theme-background-element));
  color: rgb(var(--v-theme-text-secondary));
  border: 1px solid rgb(var(--v-theme-background-border));
}

.icon-wrapper-completed {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.notification-content-wrapper {
  flex: 1;
  min-width: 0;
}

.notification-content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.2rem;
  gap: 0.5rem;
  min-width: 0;
}

.notification-content-with-badge {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
}

.notification-content-simple {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.notification-title-text {
  font-size: 0.875rem;
  font-weight: 700;
  color: rgb(var(--v-theme-text-primary));
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.35;
}

.notification-message {
  font-size: 0.75rem;
  color: rgb(var(--v-theme-text-secondary));
  margin: 0.2rem 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.notification-time {
  font-size: 0.6875rem;
  color: rgb(var(--v-theme-text-secondary));
  opacity: 0.85;
  margin: 0.35rem 0 0;
  line-height: 1.35;
}

.notification-percentage {
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.percentage-primary {
  color: rgb(var(--v-theme-primary));
}

.percentage-orange {
  color: #ea580c;
}

.notification-progress-bar {
  width: 100%;
  height: 0.375rem;
  background: rgb(var(--v-theme-background-border));
  border-radius: 9999px;
  overflow: hidden;
  margin-top: 0.65rem;
}

.notification-progress-fill {
  height: 100%;
  border-radius: 9999px;
  position: relative;
  overflow: hidden;
  transition: width 0.3s ease;
}

.progress-fill-primary {
  background: rgb(var(--v-theme-primary));
}

.progress-fill-orange {
  background: #f7923c;
}

.progress-shimmer {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.2);
  width: 100%;
  height: 100%;
  animation: shimmer 1.5s infinite;
  transform: skewX(-12deg);
}

.notification-status-badge {
  flex-shrink: 0;
  align-self: center;
}

.status-badge-text {
  display: inline-block;
  padding: 0.2rem 0.45rem;
  border-radius: 0.25rem;
  background: rgb(var(--v-theme-background-element));
  color: rgb(var(--v-theme-text-secondary));
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border: 1px solid rgb(var(--v-theme-background-border));
}

.notification-complete-icon {
  flex-shrink: 0;
  align-self: center;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 9999px;
  background: rgba(34, 197, 94, 0.18);
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
